import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { CreateCommanWay, GetCommanWay, ListCommanWay, UpdateBookingCommanWay } from "API/commonApi";
import axios, { AxiosResponse } from "axios";
import ImageUploadField from "components/ImageUploadField/ImageUploadField";
import { useFormik } from "formik";
import { isObject } from "lodash";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import UseToast from "utils/AfToast";
import handleWheel from "utils/handleOnWheel";
import { AfDataType as ADT } from "utils/TableColumnData";
import { object } from "yup";
import { Card, CardBody, CardHeader, Button, Input, Chip, Divider, Tooltip, Avatar } from "@heroui/react";

interface BookingState {
  mode?: string;
  data?: { [key: string]: any };
}

// Custom select styles for a modern look
const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "12px",
    borderColor: state.isFocused ? "#0077B6" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.2)" : "none",
    padding: "2px 4px",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "#0077B6",
    },
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? "#0077B6" : state.isFocused ? "#e0f2fe" : "white",
    color: state.isSelected ? "white" : "#1e293b",
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#0077B6",
    },
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#94a3b8",
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "#1e293b",
  }),
};

// Modern styled input component - Moved outside to prevent re-creation on each render
const ModernInput = ({
  label,
  name,
  type = "text",
  disabled = false,
  onBlurExtra = null,
  className = "",
  value,
  onChange,
  onBlur,
}: any) => (
  <div className={`space-y-1.5 ${className}`}>
    <label className="text-sm font-medium text-slate-600 whitespace-nowrap">{label}</label>
    <input
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      onBlur={async (e) => {
        onBlur(e);
        if (onBlurExtra) onBlurExtra(e);
      }}
      onWheel={type === "number" ? handleWheel : undefined}
      disabled={disabled}
      placeholder={`Enter ${label.toLowerCase()}`}
      className={`w-full px-4 py-2 rounded-xl border transition-all duration-200 outline-none
        ${
          disabled
            ? "bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
            : "bg-white border-slate-200 hover:border-primary2 focus:border-primary2 focus:ring-2 focus:ring-primary2/20"
        }
        text-slate-700 placeholder:text-slate-400`}
    />
  </div>
);

// Modern datetime input - Moved outside to prevent re-creation on each render
const ModernDateTimeInput = ({ label, name, disabled = false, minDate = "2000-01-01", value, onChange, onBlur }: any) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-slate-600">{label}</label>
    <input
      type="datetime-local"
      name={name}
      value={value || ""}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      min={minDate}
      className={`w-full px-4 py-2 rounded-xl border transition-all duration-200 outline-none
        ${
          disabled
            ? "bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
            : "bg-white border-slate-200 hover:border-primary2 focus:border-primary2 focus:ring-2 focus:ring-primary2/20"
        }
        text-slate-700`}
    />
  </div>
);

// Modern select component wrapper - Moved outside to prevent re-creation on each render
const ModernSelect = ({ label, name, options, disabled = false, className = "", value, onChange, onBlur }: any) => (
  <div className={`space-y-1.5 ${className}`}>
    <label className="text-sm font-medium text-slate-600 whitespace-nowrap">{label}</label>
    <Select
      options={options}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      name={name}
      isDisabled={disabled}
      styles={selectStyles}
      placeholder={`Select ${label.toLowerCase()}`}
      className="w-full"
    />
  </div>
);

// Info display item - Moved outside to prevent re-creation on each render
const InfoItem = ({ label, value, icon = null, className = "" }: any) => (
  <div className={`flex items-start gap-3 ${className}`}>
    {icon && (
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary2/10 to-primary3/10 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-semibold text-slate-700 mt-0.5 truncate">{value || "-"}</p>
    </div>
  </div>
);

const UpdatePOD: React.FC = ({}) => {
  let isDisabled = false;
  const navigate = useNavigate();
  const { state } = useLocation();
  const { mode = "add", data = {} } = (state || {}) as BookingState;

  const [isBranchSet, setIsBranchSet] = useState<any>();
  const [newTableData, setNewTableData] = useState<any>([]);
  const [nestedTables, setNestedTables] = useState<any>({});
  const [pincodeSection, setPincodeSection] = useState<any>("");
  const [podStatusList, setPodStatusList] = useState<any[]>([]);
  const [awbData, setAwbData] = useState<any>({});
  const [bookData, setBookData] = useState<any>({});
  const [lastPodData, setLastPodData] = useState<any>({});

  const initialValues: any = {
    awb_no: "",
    new_awb: "",
    contact_no: "",
    assign_to_delivery_agent: "",
    consignee: "",
    address: "",
    base_price: "",
    message_code: "",
    message_code_id: null,
    receiver: "",
    receiver_relation: "",
    remark: "",
    updatedAt: "",
    previous_receiver: "",
    previous_receiver_relation: "",
    previous_remark: "",
    previous_message_code: "",
    booking_date: "",
    delivery_date: "",
    system_del_date: "",
    status: "",
    is10_30: false,
    holiday: false,
    signature: "",
    rec: "",
  };
  const {
    mutate,
    isLoading: isloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((data) => CreateCommanWay(data), {
    onSuccess: (data: any) => {
      console.log(data, data.status == "SUCCESS");
      if (data.status == "SUCCESS") {
        UseToast("Data Added Successfully", "success");
        //successNavigate();
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const { mutate: mutateVoucher } = useMutation((data) => ListCommanWay("/admin/vouchure/regular/create", data, {}), {
    onSuccess: (data: any) => {},
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const { handleSubmit, handleBlur, values, handleChange, errors, setValues, setErrors, touched, dirty, setFieldValue, handleReset } =
    useFormik({
      validationSchema: object().shape({}),
      initialValues: initialValues,
      onSubmit: async (values, { resetForm }) => {
        if (!values.awb_no) {
          return UseToast("Please enter AWB No", "error");
        }
        if (!values.updatedAt) {
          return UseToast("Please select POD Date", "error");
        }

        let req = { ...values };
        console.log("Before req", { ...req });
        if (typeof req.message_code == "object") {
          req.message_code_id = req.message_code?.value;
        }
        console.log("After req", req.message_code_id);
        /*req.message_code_id =
          isObject(req?.message_code) && "value" in req?.message_code ? (req?.message_code as { value: string }).value : undefined;*/

        req.message_code =
          isObject(req?.message_code) && "value" in req?.message_code ? (req?.message_code as { label: string }).label : undefined;

        console.log(req);
        if (req.message_code_id == 0 && !req.receiver) {
          return UseToast("Please enter receiver name for Delivered status", "error");
        }
        //return;

        let tmpLabel = req.message_code.split(" - ");
        try {
          tmpLabel.shift();
          req.message_code = tmpLabel.join("");
        } catch (ignore) {}

        req.receiver_relation =
          isObject(req?.receiver_relation) && "value" in req?.receiver_relation
            ? (req?.receiver_relation as { value: string }).value.toString()
            : "";

        req.statusCode = req.message_code_id;
        if ([5, 16, 17].includes(req.message_code_id)) {
          req.new_awb = values.new_awb || "";
        }

        console.log("req", req);
        req.updatedAt = new Date(req.updatedAt).toISOString();

        if (!req.signature && !req.rec) {
          if (req.message_code) {
            /*let voucherObj = {
              lat
              long
              image
              signature
              new_awb
              pincode
              mobile
              cgnee
              address
              receiver
              receiver_relation
              remark
              awb_nos
              statusCode // code
              status_reason // reason
              message_code //reason
            }*/
            req.isWebsite = true;
            mutate({ url: "/admin/vouchure/regular/create", data: req }); // invalid vals in param

            mutate({ url: "/admin/proof_of_delivery/create", data: req });
            //window.location.reload();
            //resetForm();
          }
        } else {
          const formData = new FormData();

          // Convert values to FormData
          console.log("values for formdata", values);
          req.isWebsite = true;
          Object.keys(req).forEach((key) => {
            // Check if the value is an object with a 'value' property
            if (isObject(req[key]) && "value" in req[key]) {
              formData.append(key, req[key].value.toString());
            } else {
              formData.append(key, req[key]);
            }
          });

          // Perform your API call with FormData
          try {
            mutate({ url: "/admin/vouchure/regular/create", data: req });
            mutate({ url: "/admin/proof_of_delivery/create", data: formData });

            /*const response = await axios.post(process.env.REACT_APP_BACKEND_URL + "/admin/proof_of_delivery/create", formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });*/
            //UseToast("Data Added Successfully", "success");

            /*console.log("API response:", response.data);

            if (response.data) {
              //resetForm();
            } else {
              UseToast(response.data.message, "error");
            }*/
          } catch (error) {
            console.error("API error:", error);
          }
        }
      },
    });

  const {
    mutate: mutateAWBPOD,
    isLoading: isAWBPODloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((awbNo) => GetCommanWay("/admin/proof_of_delivery/awb_details/" + awbNo, {}, {}), {
    onSuccess: (data: any) => {
      if (data.status === "SUCCESS") {
        setAwbData(data.data.awb_details);
        setBookData(data.data.booking_details);
        Object.keys(data.data.awb_details).map((key) => {
          if (key != "updatedAt") {
            if (Object.keys(values).includes(key)) values[key] = data.data.awb_details[key];
          }
        });

        values.booking_date = moment(data.data.booking_details.date, "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD");
        Object.keys(data.data.foundProof_of_delivery).map((key) => {
          if (Object.keys(values).includes(`previous_${key}`)) {
            values[`previous_${key}`] = data.data.foundProof_of_delivery[key];
          }
        });
        setLastPodData(data.data.foundProof_of_delivery || {});
        return data.data;
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const fetchDBInputData = async () => {
    let proms = [];

    let nestedApiData = {};

    return Promise.all(proms).then((x) => {
      setNestedTables(nestedApiData);
      return 1;
    });
  };

  useQuery(["pod_status_list"], () => GetCommanWay("/admin/status_list", {}, {}), {
    keepPreviousData: true,
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        try {
          console.log(data.data);
          if (data.data) {
            let s_lst = [];
            data.data.forEach((s) => {
              if (s?.status_code !== undefined) {
                s_lst.push({
                  value: s.status_code,
                  label: s.status_code + " - " + s.reason,
                });
              }
            });
            setPodStatusList(s_lst || []);
          }
        } catch (c) {}
      }
    },
  });

  //console.log("-=======-----> newTableData", newTableData);

  return (
    <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 py-2 px-2 font-inter flex-1">
      {/* Header with AWB Input */}
      <div className=" mx-auto mb-2">
        <div className=" bg-white/80 backdrop-blur-sm rounded-3xl p-4 shadow-xl shadow-slate-200/50">
          <div className="grid grid-cols-3 md:grid-cols-1 items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary2 to-primary3 flex items-center justify-center shadow-lg shadow-primary2/25">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent">
                  Single POD Update
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">Update proof of delivery info.</p>
              </div>
            </div>

            {/* AWB Number Input - Primary Action */}
            <div className="">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-primary2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>

                <input
                  type="text"
                  name="awb_no"
                  value={values.awb_no || ""}
                  onChange={handleChange}
                  onBlur={async (e) => {
                    handleBlur(e);
                    if (e.target.value) {
                      Object.entries(initialValues).forEach(([key, value]) => {
                        setFieldValue(key, value);
                      });
                      setFieldValue("awb_no", e.target.value);
                      mutateAWBPOD(e.target.value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && values.awb_no) {
                      Object.entries(initialValues).forEach(([key, value]) => {
                        setFieldValue(key, value);
                      });
                      setFieldValue("awb_no", values.awb_no);
                      mutateAWBPOD(values.awb_no);
                    }
                  }}
                  placeholder="Enter AWB Number..."
                  className="w-full pl-12 pr-4 py-2 rounded-2xl border-2 border-slate-200 bg-slate-200
                  text-slate-700 placeholder:text-slate-400 font-medium
                  hover:border-primary2/50 focus:border-primary2 focus:ring-4 focus:ring-primary2/10
                  transition-all duration-200 outline-none text-lg"
                />
                {isAWBPODloading && (
                  <div className="absolute inset-y-0 right-4 flex items-center">
                    <div className="w-5 h-5 border-2 border-primary2 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            </div>

            <div></div>
          </div>
        </div>
      </div>

      {/* Main Content - Only show when AWB is entered */}
      {values.awb_no ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(e);
          }}
          className=" mx-auto"
        >
          <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
            {/* LEFT COLUMN - AWB Information */}
            <Card className="shadow-xl shadow-slate-200/50 border-none bg-white/80 backdrop-blur-sm">
              <CardHeader className="pb-0 pt-4 px-4 flex-col items-start">
                <div className="flex items-center gap-3 w-full">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-violet-600 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">AWB Information</h2>
                    <p className="text-xs text-slate-500">Existing Shipment & delivery details</p>
                  </div>
                  <div className="ml-auto flex gap-2 items-center bg-primary/20 px-3 py-1 rounded-xl">
                    <span className="text-sm font-semibold text-slate-500">BOOKING DATE:</span>
                    <span className="font-semibold text-primary">
                      {values.booking_date ? moment(values.booking_date).format("DD-MM-YYYY") : "-"}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <Divider className="mt-3" />
              <CardBody className="px-4 pb-4">
                {/* Shipper & Consignee Section */}
                <div className="space-y-2 mb-2">
                  <div className="grid grid-cols-2 gap-3 mb-1">
                    <div className="flex items-center gap-2 bg-white rounded-xl p-3 shadow-sm">
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider m-0">Shipper: </p>
                      <p className="text-sm font-semibold text-slate-700 truncate m-0">{bookData.shipper_sh || "-"}</p>
                    </div>
                    <div className="flex items-center gap-2 bg-white rounded-xl p-3 shadow-sm">
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider m-0">Cgnee:</p>
                      <p className="text-sm font-semibold text-slate-700 m-0">{values.consignee || "-"}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-1">
                    <div className="flex items-center gap-2 bg-white rounded-xl p-3 shadow-sm">
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider m-0">ORG: </p>
                      <p className="text-sm font-semibold text-slate-700 truncate m-0">{bookData.shipper_city || "-"}</p>
                    </div>
                    <div className="flex items-center gap-2 bg-white rounded-xl p-3 shadow-sm">
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider m-0">DEST:</p>
                      <p className="text-sm font-semibold text-slate-700 m-0">{awbData.destination || "-"}</p>
                    </div>
                  </div>
                </div>

                {/* Previous POD Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <InfoItem
                      label="Holiday"
                      value={values.holiday ? "Yes" : "No"}
                      icon={
                        <svg
                          className="w-6 h-6 text-primary2"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"
                          />
                        </svg>
                      }
                    />

                    <InfoItem
                      label="Receiver"
                      value={values.previous_receiver}
                      icon={
                        <svg className="w-4 h-4 text-primary2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      }
                    />
                    <InfoItem
                      label="Receiver Mobile"
                      value={lastPodData?.remark?.includes(" | ") ? lastPodData?.remark?.split(" | ")[0] : ""}
                      icon={
                        <svg className="w-4 h-4 text-primary2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      }
                    />
                    <InfoItem
                      label="Remark"
                      value={lastPodData?.remark?.includes(" | ") ? lastPodData?.remark?.split(" | ")[1] : lastPodData?.remark}
                      icon={
                        <svg className="w-4 h-4 text-primary2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      }
                    />
                    <InfoItem
                      label="Relation"
                      value={values.previous_receiver_relation}
                      icon={
                        <svg className="w-4 h-4 text-primary2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                      }
                    />
                  </div>
                  <div className="space-y-3">
                    <InfoItem
                      label="Current Status"
                      value={values.previous_message_code}
                      icon={
                        <svg className="w-4 h-4 text-primary2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      }
                    />
                    <InfoItem
                      label="Third Party"
                      value={awbData.thirdPartyName}
                      icon={
                        <svg className="w-4 h-4 text-primary2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      }
                    />
                    <InfoItem
                      label="RTO AWB No."
                      value={awbData.status == 5 ? awbData.returnAwb : "N/A"}
                      icon={
                        <svg className="w-4 h-4 text-primary2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                      }
                    />
                    <InfoItem
                      label="Delivery Employee"
                      value={awbData?._assign_to_delivery_agent?.name || awbData?._assign_to_delivery_agent?.username || awbData?._assign_to_delivery_agent?.agent_name}
                      icon={
                        <svg className="w-4 h-4 text-primary2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      }
                    />
                    <InfoItem
                      label="Delivery Date"
                      value={lastPodData.updatedAt}
                      icon={
                        <svg className="w-4 h-4 text-primary2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                          />
                        </svg>
                      }
                    />
                  </div>
                </div>

                {/* Status Chips */}
                <div className=" pt-4 flex gap-3 flex-wrap">
                  <Chip
                    color={awbData.is10_30 ? "success" : "default"}
                    variant={awbData.is10_30 ? "solid" : "flat"}
                    className="font-medium"
                  >
                    10:30 Delivery: {awbData.is10_30 ? "Yes" : "No"}
                  </Chip>
                  <Chip color="warning" variant="flat" className="font-semibold">
                    <span className="font-semibold">Weight: {awbData.weight} Kg</span>
                  </Chip>
                </div>
              </CardBody>
            </Card>

            {/* RIGHT COLUMN - POD Update Form & Photo Upload */}
            <div className="space-y-2">
              {/* POD Form Card */}
              <Card className="shadow-xl shadow-slate-200/50 border-none bg-slate-200/80 backdrop-blur-sm overflow-visible z-10">
                <CardHeader className="pb-0 pt-4 px-4 flex-col items-start">
                  <div className="flex items-center justify-between gap-3 w-full">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-slate-800">Update POD</h2>
                        <p className="text-xs text-slate-500">Enter new delivery information</p>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      size="md"
                      className="bg-gradient-to-r from-primary2 to-primary3 text-white font-semibold py-3 rounded-2xl shadow-lg shadow-primary2/30 hover:shadow-xl hover:shadow-primary2/40 transform hover:-translate-y-0.5 transition-all duration-200"
                      isLoading={isloading}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                        />
                      </svg>
                      Update POD
                    </Button>
                  </div>
                </CardHeader>
                <Divider className="mt-3" />
                <CardBody className="px-4 pb-4 space-y-2 overflow-visible">
                  {/* Message Code Select */}
                  <ModernSelect
                    label="Message Code"
                    name="message_code"
                    options={podStatusList}
                    disabled={isDisabled}
                    className="flex items-center gap-2 w-full"
                    value={values.message_code}
                    onChange={(e: any) => setFieldValue("message_code", e)}
                    onBlur={handleBlur}
                  />

                  {/* Conditional Fields */}
                  {[5, 17].includes(values.message_code?.value) && (
                    <ModernInput
                      label="New AWB"
                      name="new_awb"
                      disabled={isDisabled}
                      className="flex items-center gap-2 w-full"
                      value={values.new_awb}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-1 gap-4">
                    {[0, 12, 21].includes(values.message_code?.value) && (
                      <ModernInput
                        label="Receiver"
                        name="receiver"
                        disabled={isDisabled}
                        className="flex items-center gap-2 w-full"
                        value={values.receiver}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    )}

                    {[0].includes(values.message_code?.value) && (
                      <ModernSelect
                        label="Relation"
                        name="receiver_relation"
                        options={[
                          { label: "Self", value: "self" },
                          { label: "Husband/Wife", value: "Husband/Wife" },
                          { label: "Mother/Father", value: "Mother/Father" },
                          { label: "Security", value: "Security" },
                          { label: "Colleague", value: "Colleague" },
                        ]}
                        disabled={isDisabled}
                        className="flex items-center gap-2 w-full"
                        value={values.receiver_relation}
                        onChange={(e: any) => setFieldValue("receiver_relation", e)}
                        onBlur={handleBlur}
                      />
                    )}
                  </div>

                  <div className="flex md:flex-col gap-4">
                    <ModernInput
                      label="Mobile No / Remarks"
                      name="remark"
                      disabled={isDisabled}
                      value={values.remark}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />

                    <ModernDateTimeInput
                      label="POD Date & Time"
                      name="updatedAt"
                      disabled={isDisabled}
                      minDate={values.booking_date}
                      value={values.updatedAt}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                </CardBody>
              </Card>

              {/* Photo Upload Card */}
              <Card className="shadow-xl shadow-slate-200/50 border-none bg-white/80 backdrop-blur-sm overflow-hidden">
                <CardHeader className="pb-0 pt-4 px-4 flex-col items-start">
                  <div className="flex items-center gap-3 w-full">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-slate-800">Upload Photo</h2>
                      <p className="text-xs text-slate-500">Attach proof of delivery image</p>
                    </div>
                  </div>
                </CardHeader>
                <Divider className="mt-3" />
                <CardBody className="px-4 pb-4">
                  <ImageUploadField title="" propertyName="rec" values={values} setFieldValue={setFieldValue} />
                </CardBody>
              </Card>
            </div>
          </div>
        </form>
      ) : (
        /* Empty State - When no AWB is entered */
        <div className="max-w-6xl mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-12 shadow-xl shadow-slate-200/50 text-center">
            <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-700 mb-2">Enter AWB Number to Get Started</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              Type an AWB number in the search field above to load shipment details and update the proof of delivery.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdatePOD;
